import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, Signal, inject, signal } from '@angular/core';

export type V2Theme = 'light' | 'dark';
export type V2TextSize = 'sm' | 'md' | 'lg';

const THEME_KEY = 'evo.v2.theme';
const TEXT_SIZE_KEY = 'evo.v2.textSize';

const THEMES: readonly V2Theme[] = ['light', 'dark'];
const TEXT_SIZES: readonly V2TextSize[] = ['sm', 'md', 'lg'];

/**
 * The viewer's appearance preferences for the v2 shell.
 *
 * Persisted in localStorage rather than on the user record: this backend has
 * no preferences endpoint (data-gaps.md G10). That means the choice does not
 * follow anyone to a second device, which is the known cost of shipping now.
 *
 * The default text size is `sm`. evo-tss defaults to Large for 50+ pilot users
 * on a phone; this app's audience is desk-bound flight planners, and both
 * mockups open at Small.
 */
@Injectable({ providedIn: 'root' })
export class V2ThemeService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _theme = signal<V2Theme>(this.read(THEME_KEY, THEMES, 'light'));
  private readonly _textSize = signal<V2TextSize>(this.read(TEXT_SIZE_KEY, TEXT_SIZES, 'sm'));

  readonly theme: Signal<V2Theme> = this._theme.asReadonly();
  readonly textSize: Signal<V2TextSize> = this._textSize.asReadonly();

  constructor() {
    this.apply('data-theme', this._theme());
    this.apply('data-text-size', this._textSize());
  }

  setTheme(theme: V2Theme): void {
    this._theme.set(theme);
    this.write(THEME_KEY, theme);
    this.apply('data-theme', theme);
  }

  toggleTheme(): void {
    this.setTheme(this._theme() === 'dark' ? 'light' : 'dark');
  }

  setTextSize(size: V2TextSize): void {
    this._textSize.set(size);
    this.write(TEXT_SIZE_KEY, size);
    this.apply('data-text-size', size);
  }

  /**
   * A stored value can be anything -- a stale key from an older build, or
   * something a user typed into devtools. Anything not in the allowed set is
   * discarded rather than applied, so a bad value cannot brick the page.
   */
  private read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
    if (!this.isBrowser) { return fallback; }
    try {
      const stored = localStorage.getItem(key) as T | null;
      return stored && allowed.includes(stored) ? stored : fallback;
    } catch {
      return fallback;
    }
  }

  /** Storage throws outright in private browsing. A preference is never worth an exception. */
  private write(key: string, value: string): void {
    if (!this.isBrowser) { return; }
    try {
      localStorage.setItem(key, value);
    } catch {
      // The preference holds for this session and is simply not remembered.
    }
  }

  private apply(attribute: string, value: string): void {
    if (!this.isBrowser) { return; }
    document.documentElement.setAttribute(attribute, value);
  }
}
